/**
 * Mini App API client for CRM Runtime commerce (via Workers bridge).
 *
 * - Passes raw Telegram initData only (Authorization: tma …)
 * - No API keys, no privileged Supabase credentials, no direct database access from the browser
 * - Bounded timeouts; retries only safe GETs
 * - Idempotency key required for order submit
 * - Correlation ID on every request
 *
 * Ukrainian structured errors from backend (`errorUk`).
 */

import { getTelegramInitData } from "../lib/telegram.js";
import { isMiniAppCrmRuntimeEnabled } from "../lib/crm-feature-flags.js";

const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, "") ?? "";
const CRM_PROXY_PREFIX = "/api/mini-app/crm";
const DEFAULT_TIMEOUT_MS = 12_000;
const READ_RETRY = 1;

export type CrmErrorBody = {
  ok: false;
  errorUk?: string;
  code?: string;
  correlation_id?: string;
};

function buildUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return API_BASE ? `${API_BASE}${normalized}` : normalized;
}

function newCorrelationId(): string {
  return `ma-${crypto.randomUUID().slice(0, 12)}`;
}

function authHeaders(correlationId: string): HeadersInit {
  const initData = getTelegramInitData();
  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Correlation-Id": correlationId,
  };
  if (initData) {
    headers.Authorization = `tma ${initData}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    throw Object.assign(new Error(`Порожня відповідь (${response.status})`), {
      errorUk: `Порожня відповідь (${response.status})`,
    });
  }
  return JSON.parse(text) as T;
}

async function requestJson<T>(
  method: string,
  path: string,
  opts: {
    body?: unknown;
    timeoutMs?: number;
    retrySafeRead?: boolean;
    idempotencyKey?: string;
  } = {},
): Promise<T> {
  const correlationId = newCorrelationId();
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const isRead = method === "GET" || method === "HEAD";
  const attempts = isRead && opts.retrySafeRead !== false ? READ_RETRY + 1 : 1;

  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const headers: Record<string, string> = {
        ...(authHeaders(correlationId) as Record<string, string>),
      };
      if (opts.body != null) headers["Content-Type"] = "application/json";
      if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;

      const init: RequestInit = {
        method,
        headers,
        signal: controller.signal,
        credentials: "same-origin",
      };
      if (opts.body != null) {
        init.body = JSON.stringify(opts.body);
      }
      const response = await fetch(buildUrl(`${CRM_PROXY_PREFIX}${path}`), init);

      const data = await parseJson<T & Partial<CrmErrorBody>>(response);
      if (!response.ok || (data as CrmErrorBody).ok === false) {
        const errUk =
          (data as CrmErrorBody).errorUk ||
          `Помилка CRM (${response.status})`;
        throw Object.assign(new Error(errUk), {
          errorUk: errUk,
          code: (data as CrmErrorBody).code,
          correlation_id: (data as CrmErrorBody).correlation_id || correlationId,
          status: response.status,
        });
      }
      return data;
    } catch (err) {
      lastErr = err;
      if (i < attempts - 1 && isRead) continue;
      throw lastErr;
    } finally {
      clearTimeout(timer);
    }
  }
  throw lastErr;
}

export function isCrmRuntimeClientActive(): boolean {
  return isMiniAppCrmRuntimeEnabled();
}

export async function crmHealth() {
  return requestJson<{
    ok: true;
    owner: string;
    flags: Record<string, boolean>;
    adapters: Record<string, { status: string; labelUk: string }>;
  }>("GET", "/health");
}

export async function crmSession() {
  return requestJson<{
    ok: true;
    customer: { id: string; nameUk: string; phoneMasked: string | null };
    feature_flags: Record<string, boolean>;
    telegram_user: { id: number; first_name: string };
    correlation_id: string;
  }>("GET", "/session");
}

export async function crmProductList(params?: { q?: string; category?: string }) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set("q", params.q);
  if (params?.category) qs.set("category", params.category);
  const q = qs.toString();
  return requestJson<{
    ok: true;
    products: Array<{
      id: string;
      titleUk: string;
      priceUah: number;
      available: boolean;
      sku: string;
      categoryUk?: string | null;
    }>;
    categories: Array<{ id: string; titleUk: string }>;
  }>("GET", `/products${q ? `?${q}` : ""}`);
}

export async function crmProductDetail(id: string) {
  return requestJson<{
    ok: true;
    product: {
      id: string;
      titleUk: string;
      priceUah: number;
      available: boolean;
      variants: unknown[];
      similar: unknown[];
      boughtTogether: unknown[];
      completeOrder: unknown[];
      recommendationLabelsUk: Record<string, string> | null;
    };
  }>("GET", `/products/${encodeURIComponent(id)}`);
}

export async function crmValidateCart(lines: Array<{ productId: string; qty: number; clientPriceUah?: number }>) {
  return requestJson<{
    ok: true;
    lines: Array<{ productId: string; qty: number; unitPriceUah: number; lineTotalUah: number; titleUk: string }>;
    unavailable: unknown[];
    subtotalUah: number;
  }>("POST", "/cart/validate", { body: { lines }, retrySafeRead: false });
}

export async function crmCreateOrderDraft(body: Record<string, unknown>) {
  return requestJson<{ ok: true; draft: { id: string } }>("POST", "/orders/draft", {
    body,
    retrySafeRead: false,
  });
}

export async function crmSubmitOrder(body: {
  idempotencyKey: string;
  draftId?: string;
  lines?: Array<{ productId: string; qty: number }>;
  contact?: { fullName: string; phone: string };
  delivery?: Record<string, unknown>;
  payment?: Record<string, unknown>;
}) {
  return requestJson<{
    ok: true;
    order: { id: string; status: string; source: string };
    recovered?: boolean;
  }>("POST", "/orders", {
    body,
    idempotencyKey: body.idempotencyKey,
    retrySafeRead: false,
  });
}

export async function crmListOrders() {
  return requestJson<{
    ok: true;
    orders: Array<{ id: string; status: string; totalUah: number; createdAt: string }>;
  }>("GET", "/orders");
}

export async function crmGetOrder(orderId: string) {
  return requestJson<{
    ok: true;
    order: {
      id: string;
      status: string;
      timeline: Array<{ at: string; titleUk: string }>;
      payment: unknown;
      shipping: unknown;
    };
  }>("GET", `/orders/${encodeURIComponent(orderId)}`);
}

export async function crmTrackEvent(type: string, meta?: Record<string, unknown>) {
  return requestJson<{ ok: true }>("POST", "/events", {
    body: { type, meta },
    retrySafeRead: false,
  });
}

/** Local cart cache — no secrets; presentation only. */
const CART_CACHE_KEY = "asiastyle.miniapp.crm.cart.v1";

export type LocalCartLine = { productId: string; qty: number; titleUk?: string; clientPriceUah?: number };

export function loadLocalCartCache(): LocalCartLine[] {
  try {
    const raw = localStorage.getItem(CART_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LocalCartLine[];
    return Array.isArray(parsed) ? parsed.filter((l) => l.productId && l.qty > 0) : [];
  } catch {
    return [];
  }
}

export function saveLocalCartCache(lines: LocalCartLine[]): void {
  localStorage.setItem(CART_CACHE_KEY, JSON.stringify(lines.slice(0, 30)));
}

export function clearLocalCartCache(): void {
  localStorage.removeItem(CART_CACHE_KEY);
}
